import { useNavigate, useParams } from 'react-router-dom';
import React, { useContext, useEffect, useState } from 'react';
import { usePurchaseOrderApiV1 } from '../../../clients/quipli-service/purchase-order/purchase-order-api-v1';
import {
  PurchaseOrder,
  PurchaseOrderPaymentMethod,
  PurchaseOrderProductLineItem,
  PurchaseOrderStatus,
  toUpsertPurchaseOrderProductRequest,
} from '../../../clients/quipli-service/purchase-order/purchase-order';
import { toast } from '../../../util/toasts';
import { TenantContext } from '../../../context/tenant-context';
import { StickyHeader } from '../../../components/sticky-header/sticky-header';
import classNames from 'classnames';
import { VendorOrderSelect } from '../../vendor/vendor-order-select';
import { OrderSection } from '../../order/order-section/order-section';
import { PurchaseOrderLineItems } from '../purchase-order-line-items';
import { Location } from '../../../clients/quipli-service/location/location';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Currency } from 'dinero.js';
import { PurchaseOrderStatusTransitionButtons } from '../status-transition-button/purchase-order-status-transition-button';
import { OrderStatusTimeline } from '../../order/order-status-timeline/order-status-timeline';
import { PurchaseOrderDetails } from '../purchase-order-details/purchase-order-details';
import { PurchasePaymentSelect } from '../purchase-payment-select';
import { PurchaseLineItemForm } from '../components/purchase-line-item-form';
import { DateTime } from 'luxon';
import { Admin } from '../../../clients/quipli-service/admin/admin';
import { QboPurchaseOrder } from '../../qbo/qbo-purchase-order';
import { Typography } from '../../../components/typography/typography';

export const PurchaseOrderView = () => {
  const { purchaseOrderId } = useParams();
  const { getPurchaseOrderById, updatePurchaseOrder } = usePurchaseOrderApiV1();
  const { getLocationById } = useAPI();
  const { contextPath } = useContext(TenantContext);
  const navigate = useNavigate();

  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrder>();
  const [location, setLocation] = useState<Location>();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<PurchaseOrderPaymentMethod>();
  const [purchasedAt, setPurchasedAt] = useState<DateTime>();
  const [poNumber, setPoNumber] = useState<string>();
  const [purchasedBy, setPurchasedBy] = useState<Admin>();
  const [productItems, setProductItems] = useState<PurchaseOrderProductLineItem[]>([]);

  const redirectFailure = (msg: string) => {
    toast(msg, 'error');
    navigate(`${contextPath}manager/purchase-orders`);
  };

  function updateState(po: PurchaseOrder) {
    setPurchaseOrder(po);
    setPaymentMethod(po.paymentMethod);
    setPurchasedAt(po.purchasedAt);
    setPoNumber(po.poNumber);
    setPurchasedBy(po.purchasedBy);
    setProductItems(po.products);
  }

  const fetchPurchaseOrder = async () => {
    if (!purchaseOrderId) {
      return;
    }

    try {
      const res = await getPurchaseOrderById(purchaseOrderId);
      updateState(res);
      return res;
    } catch (e) {
      redirectFailure('Failed to load purchase order');
    }
  };

  const reset = () => {
    setPaymentMethod(purchaseOrder?.paymentMethod);
    setPurchasedAt(purchaseOrder?.purchasedAt);
    setPoNumber(purchaseOrder?.poNumber);
    setPurchasedBy(purchaseOrder?.purchasedBy);
    setProductItems(purchaseOrder?.products || []);
  };

  const handleSubmit = async () => {
    // Submit the order
    if (isSubmitting || !purchaseOrderId) {
      return;
    }

    setIsSubmitting(true);
    try {
      const updated = await updatePurchaseOrder(purchaseOrderId, {
        products: productItems.map(toUpsertPurchaseOrderProductRequest),
        purchasedBy: purchasedBy?.adminId,
        purchasedAt: purchasedAt,
        paymentMethodId: paymentMethod?.id,
        poNumber,
      });
      updateState(updated);
      setIsEditing(false);
      toast('Purchase order updated successfully.', 'success');
    } catch (e) {
      toast('Failed to update purchase order.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      const res = await fetchPurchaseOrder();

      try {
        if (res) {
          const loc = await getLocationById(res.locationId);
          setLocation(loc);
        }
      } catch (e) {
        redirectFailure('Failed to load location');
      }
    };

    if (!purchaseOrderId) {
      redirectFailure('Invalid purchase order id');
      return;
    }

    init();
  }, []);
  return (
    <div className="px-[10px]">
      {purchaseOrder && location && (
        <>
          <StickyHeader title="Purchase Order">
            <PurchaseOrderStatusTransitionButtons
              order={purchaseOrder}
              isDisabled={isEditing}
              reload={fetchPurchaseOrder}
            />
          </StickyHeader>

          <div
            className={classNames('grid grid-cols-1 lg:grid-cols-3 gap-y-[15px] lg:gap-y-0 lg:gap-x-[15px] mt-[15px]')}
          >
            <div className="col-span-2 flex flex-col gap-y-[15px]">
              <PurchaseOrderStatusView purchaseOrder={purchaseOrder} />

              {isEditing && (
                <PurchaseLineItemForm
                  openOnStart={false}
                  location={location}
                  onAddItem={(item) => setProductItems((prev) => [...prev, item])}
                />
              )}

              <PurchaseOrderLineItems
                productLineItems={productItems}
                setProductLineItems={setProductItems}
                currency={location?.currency as Currency}
                canUpdateOrder={purchaseOrder.status === PurchaseOrderStatus.Requested}
                isEditing={isEditing}
                isSubmitting={isSubmitting}
                onClickEdit={() => setIsEditing(true)}
                onClickCancel={() => {
                  reset();
                  setIsEditing(false);
                }}
                onClickSubmit={handleSubmit}
              />

              <QboPurchaseOrder purchaseOrder={purchaseOrder} />
            </div>

            <div className="flex flex-col gap-y-[15px]">
              <OrderSection className="flex flex-col gap-[12px]" header="Location">
                <Typography type="text-sm" className="text-quipli-black-800">
                  {location.name}
                </Typography>
              </OrderSection>

              <VendorOrderSelect
                className="lg:order-1 order-2"
                vendor={purchaseOrder.vendor}
                canRemoveVendor={false}
                isEditMode={isEditing}
              />

              <PurchaseOrderDetails
                className="lg:order-2 order-3"
                isEditing={isEditing}
                date={purchasedAt}
                poNumber={poNumber}
                purchasedBy={purchasedBy}
                setDate={setPurchasedAt}
                setPoNumber={setPoNumber}
                setPurchasedBy={setPurchasedBy}
              />

              <PurchasePaymentSelect
                className="lg:order-3 order-1"
                isEditing={isEditing}
                selectedPaymentMethod={paymentMethod}
                onSelect={setPaymentMethod}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const PurchaseOrderStatusView = ({ purchaseOrder }: { purchaseOrder: PurchaseOrder }) => {
  const isApproved = purchaseOrder.status !== PurchaseOrderStatus.Requested;
  const isReceived = purchaseOrder.status === PurchaseOrderStatus.Received;

  return (
    <OrderSection header="Status">
      <OrderStatusTimeline
        entries={[
          {
            text: 'Requested',
            isActive: true,
            type: purchaseOrder.status === PurchaseOrderStatus.Cancelled ? 'cancelled' : 'completed',
          },
          {
            text: 'Approved',
            isActive: isApproved,
            type:
              purchaseOrder.status === PurchaseOrderStatus.Cancelled
                ? 'cancelled'
                : isApproved
                ? 'completed'
                : undefined,
          },
          {
            text: 'Received',
            isActive: isReceived,
            type:
              purchaseOrder.status === PurchaseOrderStatus.Cancelled
                ? 'cancelled'
                : isReceived
                ? 'completed'
                : undefined,
          },
        ]}
      />
    </OrderSection>
  );
};
